import config from "../config"
import { findIndex, get, reject } from "lodash"
import jwt, { JwtPayload } from "jsonwebtoken"
import EventEmmitter from "./EventEmitter"
import { AuthProvider, HttpError, UserIdentity } from "react-admin"

export class Profile implements UserIdentity {
  id: string = ""
  token: string = "" 
  email: string = ""
  refreshToken: string = ""
  permissions: string[] = []
  expiredAt?: Date  
  fullName?: string | undefined
}

class MyAuthProvider extends EventEmmitter implements AuthProvider {
  __refreshTimout?: NodeJS.Timeout

  getProfiles(): Profile[] {
    const profiles = JSON.parse(localStorage.getItem("profiles") || "[]")
    if (!profiles.length) {
      this.setProfiles(Object.values(profiles))
      return Object.values(profiles)
    }
    return profiles
  }
  setProfiles(profiles: Profile[]) {
    localStorage.setItem("profiles", JSON.stringify(profiles || []))
  }
  getToken(): string | undefined {
    return get(this.getProfiles()[0], "token")
  }
  getProfile(): Profile | null {
    return this.getProfiles()[0]
  }
  getRefreshToken() {
    return get(this.getProfiles()[0], "refreshToken")
  }
  async setActiveAccount(token: string, { refreshToken, profile }: any = {}): Promise<Profile> {
    const { uuid: id, exp = 0 } = jwt.decode(token) as JwtPayload
    const expiredAt = exp * 1000 - 15000
    const now = Date.now()
    const refreshIn = expiredAt - now
    const profiles = this.getProfiles()

    const index = findIndex(profiles, ["id", id])
    clearTimeout(this.__refreshTimout)
    this.__refreshTimout = setTimeout(() => this.refresh, refreshIn)

    if (index !== -1) {
      const account = { ...profiles[index], ...profile, id }
      account.refreshToken = refreshToken || account.refreshToken
      account.expiredAt = expiredAt
      account.token = token      
      account.fullName = account.name

      profiles.splice(index, 1)
      profiles.unshift(account)
      this.setProfiles(profiles)

      if (index !== 0)
        this.emit("profiles", profiles)
    } else {
      profiles.unshift({ id, ...profile, token, refreshToken, expiredAt })
      this.setProfiles(profiles)
      this.emit("profiles", profiles)
    }
    if (refreshIn <= 0) {
      return this.refresh()
    }
    this.emit("token", token)
    return profiles[0]
  }
  removeAccount(profile: Profile | null) {
    if (profile) {
      const profiles = reject(this.getProfiles(), ({ id }) => id === profile.id)
      this.setProfiles(profiles)
    }
    this.emit("profiles", this.getProfiles())
  }
  loginAs(id: string) {
    const request = new Request(`${config.apiUrl}/users/${id}/authenticate`, {
      method: "POST",
      body: "{}",
      headers: new Headers({
        "Content-Type": "application/json",
        "Authorization": this.getToken() || ""
      }),
    })
    return fetch(request).then(response => {
      if (response.status === 401) {
        return this.refresh()
      }
      if (response.status < 200 || response.status >= 300) {
        throw new HttpError(response.statusText || `Status ${response.status}`, response.status)
      }
      return response.json()
    })
      .then(({ accessToken, ...payload }) => this.setActiveAccount(accessToken, payload))
  }
  refresh(): Promise<Profile> {
    const refreshToken = this.getRefreshToken()
    if (refreshToken) {
      const request = new Request(`${config.apiUrl}/authenticate/refresh`, {
        method: "POST",
        body: JSON.stringify({ refreshToken }),
        headers: new Headers({ "Content-Type": "application/json" }),
      })
      return fetch(request).then(response => {
        if (response.status < 200 || response.status >= 300) {
          throw new HttpError(response.statusText || `Status ${response.status}`, response.status)
        }
        return response.json()
      }).then(({ token, ...payload }) => this.setActiveAccount(token, payload))
    } else {
      this.logout()
      throw new Error("Not authenicated")
    }
  }
  login(
    { username, password }: 
    { username?: string, password?: string }): Promise<Profile> {
    const request = new Request(`${config.apiUrl}/authenticate`, {
      method: "POST",
      body: JSON.stringify({ email: username, password }),
      headers: new Headers({ "Content-Type": "application/json" }),
    })
    return fetch(request).then(response => {
      if (response.status < 200 || response.status >= 300) {
        throw new HttpError(response.statusText || `Status ${response.status}`, response.status)        
      }
      return response.json()
    }).then(({ accessToken, ...payload }) => this.setActiveAccount(accessToken, payload))
  }
  logout() {
    this.removeAccount(this.getProfile())
    setTimeout(() => {
      if (document.location.pathname !== "/login") {
        document.location = "/login"
      }
    }, 2000)
    return Promise.resolve("/login")
  }
  checkAuth() {
    return this.getToken() ? Promise.resolve() : Promise.reject("/login")
  }
  async checkError(error: any): Promise<void> {
    const { status } = error
    if (status === 401) {
      await this.logout()
    }
  }
  async getIdentity?(): Promise<UserIdentity> {
    return (await this.getProfile()) as UserIdentity
  }
  getPermissions(params: any) {
    const permissions = get(this.getProfile(), "permissions", ["guest"])
    return permissions ? Promise.resolve(permissions) : Promise.reject()
  }  
}
const authProvider = new MyAuthProvider()
export default authProvider;
